import reference_config from "@/components/Reference/config";
import stored_file_config from "@/components/StoredFile/config";
import related_product_config from "@/components/RelatedProduct/config";

/**
 * « Produits spéciaux » réunit deux populations sans grand-chose en commun : les produits
 * parents (déclinaisons, lots) et la nature d'offre « Location pour compte ». Cette dernière
 * n'a ni stock, ni référence unique, ni prix de vente — les colonnes de parc et de tarif
 * sortiraient systématiquement vides. Quand l'utilisateur filtre dessus, on les retire.
 *
 * Le prédicat reçoit l'état courant des filtres étendus (cf. buildListFields dans
 * AdminTableSync) : hors filtre, les colonnes restent, la liste étant alors hétérogène.
 */
const hiddenForLocationForAccount = (filters) => filters.type !== 'location_for_account'

export default {
  storeNamespace: 'product',
  authFeature: 'product',
  listRouteName: "ParentProductList",
  editRouteName: "ProductEdit",
  detailRouteName: "ProductDetail",
  hideActionDeleteButton: true,
  useNewModal: true,
  useEditModal: true,
  formComponentName: 'ProductForm',
  csvExportFilenamePrefix: 'produits',
  titleField: 'name',
  showExtendedFilters: true,
  fields: [
    {
      name: 'type',
      label: "type",
      type: 'select',
      inList: true,
      extendedFilter: true,
      sortable: true,
    },
    {
      name: 'type',
      label: "type",
      type: 'select',
      inForm: true,
      required: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      formatter: (context, value) => value ? (context.$store.getters['product/getType'](value) ? context.$store.getters['product/getType'](value).text : value) : null
    },
    {
      name: 'name',
      label: "Nom",
      type: 'text',
      inForm: true,
      required: true,
      inList: true,
      sortable: true,
      filterIn: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'parent_product_type',
      label: "Type de produit parent",
      type: 'select',
      inList: true,
      extendedFilter: true,
      sortable: true,
    },
    {
      name: 'parent_product_type',
      label: "Type de produit parent",
      type: 'select',
      inForm: true,
      required: false,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      formatter: (context, value) => value ? context.$store.getters['product/getParentTypesForSelect'](value).text : null
    },
    {
      name: 'parents',
      label: "Parents",
      type: 'group_repeater',
      inForm: true,
      inDetail: true,
      addGroupCondition: (field_name, form) => form[field_name].filter(group => group.child_product_type == 'product_variation').length === 0,
      addGroupConditionError: "Un produit ne peut pas être une déclinaison de plusieurs produits parents",
      fields: [
        {
          name: 'child_product_type',
          label: "Type de produit enfant",
          type: 'select',
          inForm: true,
        },
        {
          name: 'parent_product_id',
          label: "Produit parent",
          type: 'chosen',
          inForm: true,
        }
      ]
    },
    {
      name: 'internal_code',
      label: "Code interne",
      type: 'text',
      inForm: true,
      required: true,
      inList: true,
      sortable: true,
      filterIn: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
    },
    {
      name: 'custom_bar_code',
      label: "Code barre personnalisé",
      type: 'text',
      inForm: true,
      required: true,
      inList: true,
      inListCondition: hiddenForLocationForAccount,
      sortable: true,
      filterIn: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
    },
    {
      name: 'picture',
      label: "Photo principale",
      type: 'file',
      inForm: true,
      required: true,
      inDetail: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
    },
    {
      name: 'other_pictures',
      label: "Autres photos",
      type: 'group_repeater',
      inForm: true,
      inDetail: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
      fields: [{
        name: 'file',
        label: "Fichier",
        type: 'file',
        inForm: true,

      }]
    },
    {
      name: 'other_pictures',
      label: "Autres photos",
      type: 'list',
      icone: 'folder',
      config: stored_file_config,
      fields: ['file']
    },
    {
      name: 'equipment_group_id',
      label: "Famille",
      type: 'chosen',
      inForm: true,
      required: true,
      inFormCondition: (form) => form.type && form.child_product_type != 'product_variation',
    },
    {
      name: 'equipment_group',
      label: "Famille",
      type: 'select',
      inList: true,
      extendedFilter: true,
      sortable: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      formatter: (context, value) => value && value.name ? value.name : null
    },
    {
      name: 'rent_count',
      label: "En location",
      type: 'number',
      inForm: false,
      inList: true,
      inListCondition: hiddenForLocationForAccount,
      // Sans objet pour la nature « Location pour compte » : le matériel n'entre jamais
      // en stock et n'a pas de référence unique. Sortir vide plutôt que zéro, qui se
      // lirait comme un comptage réel.
      formatter: (context, value, item) => item && item.type == 'location_for_account' ? null : value,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'maintenance_count',
      label: "En maintenance",
      type: 'number',
      inForm: false,
      inList: true,
      inListCondition: hiddenForLocationForAccount,
      // Sans objet pour la nature « Location pour compte » : le matériel n'entre jamais
      // en stock et n'a pas de référence unique. Sortir vide plutôt que zéro, qui se
      // lirait comme un comptage réel.
      formatter: (context, value, item) => item && item.type == 'location_for_account' ? null : value,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'lost_count',
      label: "Perdu/Volés",
      type: 'number',
      inForm: false,
      inList: true,
      inListCondition: hiddenForLocationForAccount,
      // Sans objet pour la nature « Location pour compte » : le matériel n'entre jamais
      // en stock et n'a pas de référence unique. Sortir vide plutôt que zéro, qui se
      // lirait comme un comptage réel.
      formatter: (context, value, item) => item && item.type == 'location_for_account' ? null : value,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'trash_count',
      label: "Au rebut",
      type: 'number',
      inForm: false,
      inList: true,
      inListCondition: hiddenForLocationForAccount,
      // Sans objet pour la nature « Location pour compte » : le matériel n'entre jamais
      // en stock et n'a pas de référence unique. Sortir vide plutôt que zéro, qui se
      // lirait comme un comptage réel.
      formatter: (context, value, item) => item && item.type == 'location_for_account' ? null : value,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'supplier_id',
      label: "Fournisseur",
      type: 'chosen',
      inForm: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
      required: true,
    },
    {
      name: 'brand',
      label: "Marque",
      type: 'text',
      inForm: true,
      required: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
    },
    {
      name: 'model',
      label: "Modèle",
      type: 'text',
      inForm: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
    },
    {
      name: 'stock_mini',
      label: "Stock minimum",
      type: 'number',
      inForm: true,
      required: true,
      inList: false,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      inFormCondition: (form) => form.parent_product_type != 'product_with_variations'
    },
    {
      name: 'calibration_periodicity',
      label: "Périodicité d'étalonnage / Contrôle obligatoire (jours)",
      type: 'number',
      inForm: true,
      inFormCondition: (form) => form.type && form.type == 'renting' && form.parent_product_type != 'product_with_variations',
    },
    {
      name: 'calibration_provider_id',
      label: "Organisme d'étalonnage",
      type: 'chosen',
      inForm: true,
      inFormCondition: (form) => form.type && form.type == 'renting' && form.parent_product_type != 'product_with_variations',
      inList: false,
      inDetail: false,
      inCsvExport: false,
      inPdfExport: false,
    },
    {
      name: 'calibration_provider',
      label: "Organisme d'étalonnage",
      type: 'select',
      inForm: false,
      inList: false,
      inDetail: true,
      detailLink: 'CompanyDetail',
      inCsvExport: true,
      inPdfExport: true,
      formatter: (context, value) => value && value.name ? value.name : null
    },
    {
      name: 'imputation_number',
      label: "Numéro d'imputation",
      type: 'text',
      inForm: true,
      inFormCondition: (form) => form.type && form.type == 'renting' && form.parent_product_type != 'product_with_variations',
      inList: false,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'applications',
      label: "Applications",
      type: 'textarea',
      inForm: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
    },
    {
      name: 'characteristics',
      label: "Caractéristiques",
      type: 'textarea',
      inForm: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
    },
    {
      name: 'security_rules',
      label: "Règles de sécurité",
      type: 'textarea',
      inForm: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
    },
    {
      name: 'technical_sheet',
      label: "Fiche technique",
      type: 'file',
      inForm: true,
      inDetail: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
    },
    {
      name: 'video',
      label: "Vidéo Youtube",
      type: 'text',
      inForm: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
    },
    {
      name: 'documents',
      label: "Documents téléchargeables",
      type: 'group_repeater',
      inForm: true,
      inDetail: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
      fields: [{
        name: 'file',
        label: "Fichier",
        type: 'file',
        inForm: true,
      }]
    },
    {
      name: 'status_id',
      label: "Statut",
      type: 'select',
      inForm: true,
      required: true,
      inFormCondition: (form) => form.id,
      inList: false,
      inDetail: false,
      inPdfExport: false,
    },
    {
      name: 'comment',
      label: "Commentaire",
      type: 'textarea',
      inForm: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'status',
      label: "Statut",
      type: 'select',
      inForm: false,
      inList: true,
      tabFilter: true,
      tabFilterDefaultValue: 'product_on_sale',
      key: 'status_slug',
      sortable: true,
      filterIn: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      formatter: (context, value) => value && value.name ? value.name : null
    },
    {
      name: 'related_products',
      label: "Produits associés",
      type: 'group_repeater',
      inForm: true,
      inFormCondition: (form) => form.child_product_type != 'product_variation',
      fields: [{
        name: 'id',
        label: "Produit à la vente",
        type: 'chosen',
        inForm: true,
      }]
    },
    {
      name: 'purchase_price',
      label: "Prix d'achat unitaire HT",
      type: 'number',
      step: 0.01,
      inForm: true,
      inFormCondition: (form) => form.parent_product_type != 'product_with_variations',
      required: true,
      inDetail: true,
      inPdfExport: true,
      formatter: (context, value) => value ? parseFloat(value).toFixed(2) + '€' : null
    },
    {
      name: 'sale_price',
      label: "Prix de vente HT",
      type: 'number',
      step: 0.01,
      inForm: true,
      required: true,
      inFormCondition: (form) => form.type && form.type == 'selling',
    },
    {
      name: 'sale_price',
      label: "Prix de vente HT",
      type: 'number',
      step: 0.01,
      inDetail: true,
      inList: true,
      inListCondition: hiddenForLocationForAccount,
      inPdfExport: true,
      // Une location pour compte n'a pas de prix de vente : son tarif est négocié demande
      // par demande, il ne vit pas sur le produit.
      formatter: (context, value, item) => item && item.type == 'location_for_account'
        ? null
        : (value ? parseFloat(value).toFixed(2) + '€' : null)
    },
    {
      name: 'sale_price_1',
      label: "Prix de vente HT (1 à 30 jours)",
      type: 'number',
      step: 0.01,
      inForm: true,
      required: true,
      inFormCondition: (form) => form.type && form.type == 'renting' && form.parent_product_type != 'product_with_variations',
    },
    {
      name: 'sale_price_2',
      label: "Prix de vente HT (31 à 90 jours)",
      type: 'number',
      step: 0.01,
      inForm: true,
      required: true,
      inFormCondition: (form) => form.type && form.type == 'renting' && form.parent_product_type != 'product_with_variations',
    },
    {
      name: 'sale_price_3',
      label: "Prix de vente HT (91 à 365 jours)",
      type: 'number',
      step: 0.01,
      inForm: true,
      required: true,
      inFormCondition: (form) => form.type && form.type == 'renting' && form.parent_product_type != 'product_with_variations',
    },
    {
      name: 'sale_price_4',
      label: "Prix de vente HT (366 jours à 4 ans)",
      type: 'number',
      step: 0.01,
      inForm: true,
      required: true,
      inFormCondition: (form) => form.type && form.type == 'renting' && form.parent_product_type != 'product_with_variations',
    },
    {
      name: 'sale_price_5',
      label: "Prix de vente HT (au-delà de 4 ans)",
      type: 'number',
      step: 0.01,
      inForm: true,
      required: true,
      inFormCondition: (form) => form.type && form.type == 'renting' && form.parent_product_type != 'product_with_variations',
    },
    {
      name: 'weight',
      label: "Poids (kg)",
      type: 'number',
      step: 0.1,
      inForm: true,
      required: true,
      inFormCondition: (form) => form.type && form.type == 'renting' && form.parent_product_type != 'product_with_variations',
      inDetail: true,
      inPdfExport: true,
      formatter: (context, value) => (value ? parseFloat(value).toFixed(1) : 0) + 'kg'
    },
    {
      name: 'return_mode',
      label: "Mode de retour",
      type: 'select',
      inForm: true,
      inFormCondition: (form) => form.type && form.type == 'renting' && form.parent_product_type != 'product_with_variations',
      required: true,
    },
    {
      name: 'created_at',
      label: "Création",
      type: 'date',
      inForm: false,
      inList: false,
      inDetail: true,
      inPdfExport: false,
      formatter: (context, value) => value ? context.$moment(value).format("dddd, DD MMMM YYYY à H:mm") : null
    },
    {
      name: 'updated_at',
      label: "Dernière modification",
      type: 'date',
      inForm: false,
      inList: false,
      inDetail: true,
      inPdfExport: false,
      formatter: (context, value) => value ? context.$moment(value).format("dddd, DD MMMM YYYY à H:mm") : null
    },
    {
      name: 'created_at',
      label: "Création",
      type: 'date',
      inCsvExport: true,
      formatter: (context, value) => value ? context.$moment(value).format("DD/MM/YYYY") : null
    },
    {
      name: 'updated_at',
      label: "Dernière modification",
      type: 'date',
      inCsvExport: true,
      formatter: (context, value) => value ? context.$moment(value).format("DD/MM/YYYY") : null
    },
    {
      name: 'unique_references',
      label: "Références uniques",
      type: 'list',
      inDetail: true,
      icone: 'id-wrench',
      config: reference_config,
      fields: [
        'internal_code',
        'supplier',
        'brand',
        'model',
        'status',
      ]
    }
  ]
}



// WEBPACK FOOTER //
// ./src/components/Product/config-parent.js